import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { useSession } from '../../lib/useSession';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { LIST_MAX_HEIGHT, Table, type Column } from '../../components/Table';
import { errorMessage, formatDateTime } from '../members/hooks';
import { buildAuditQuery, EMPTY_FILTERS, type Filters } from './query';
import { ACTION_FILTERS, actionLabel, actorLabel, originLabel, type AuditEntry, type AuditPage as AuditPageData } from './types';

function summarize(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value !== 'object') return String(value);
  const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return '—';
  return entries
    .slice(0, 2)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ');
}

export function AuditPage() {
  const { activeOrg } = useSession();
  const orgId = activeOrg?.orgId;
  const ownOnly = activeOrg?.role === 'seller';

  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<AuditEntry | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const canSeeMembers = activeOrg?.role === 'owner' || activeOrg?.role === 'admin';

  // Los registros guardan solo el uid del autor: Owner y Admin lo ven como email usando la lista de miembros.
  useEffect(() => {
    if (!canSeeMembers) return;
    let cancelled = false;
    api
      .get<{ items: { uid: string; email: string | null }[] }>('/v1/members', { orgId })
      .then((res) => {
        if (!cancelled) setNames(Object.fromEntries(res.items.filter((m) => m.email).map((m) => [m.uid, m.email as string])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [canSeeMembers, orgId]);
  const closeDetail = useCallback(() => setDetail(null), []);

  const load = useCallback(
    async (cursor: string | null) => {
      setError(null);
      if (cursor) setLoadingMore(true);
      else setLoading(true);
      try {
        const page = await api.get<AuditPageData>(buildAuditQuery(filters, cursor), { orgId });
        setItems((prev) => (cursor ? [...prev, ...page.items] : page.items));
        setNextCursor(page.nextCursor);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filters, orgId],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  function applyFilters(e: FormEvent) {
    e.preventDefault();
    if (draft.from && draft.to && draft.from > draft.to) {
      setError('La fecha “desde” debe ser anterior a la fecha “hasta”.');
      return;
    }
    setFilters({ ...draft });
  }

  const columns: Column<AuditEntry>[] = [
    { key: 'at', header: 'Fecha', render: (e) => <span className="whitespace-nowrap tabular-nums">{formatDateTime(e.at)}</span> },
    { key: 'actor', header: 'Autor', render: (e) => actorLabel(e.actor, names) },
    { key: 'origin', header: 'Origen', render: (e) => originLabel(e.origin) },
    { key: 'action', header: 'Acción', render: (e) => actionLabel(e.action) },
    { key: 'resource', header: 'Recurso', mono: true, render: (e) => <span className="break-all text-xs">{e.resource}</span> },
    { key: 'after', header: 'Después', render: (e) => <span className="line-clamp-2 text-xs text-ink-muted">{summarize(e.after)}</span> },
    {
      key: 'detail',
      header: '',
      align: 'right',
      render: (e) => (
        <Button variant="ghost" size="sm" onClick={() => setDetail(e)} aria-label={`Ver detalle de ${actionLabel(e.action)} del ${formatDateTime(e.at)}`}>
          Detalle
        </Button>
      ),
    },
  ];

  return (
    <section className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-bold text-ink">Auditoría</h1>
        <p className="text-sm text-ink-muted">
          {ownOnly ? 'Ves solo las acciones que hiciste tú.' : 'Quién hizo qué, cuándo y desde dónde.'}
        </p>
      </div>

      <form onSubmit={applyFilters} className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-white p-3" aria-label="Filtros de auditoría">
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
          Acción
          <select
            className="rounded-sm border border-line bg-white px-2 py-2 text-sm text-ink"
            value={draft.action}
            onChange={(e) => setDraft({ ...draft, action: e.target.value })}
          >
            {ACTION_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </label>
        <div className="w-48">
          <Input label="Recurso" placeholder="slots/K7MX" value={draft.resource} onChange={(e) => setDraft({ ...draft, resource: e.target.value })} />
        </div>
        <div className="w-40">
          <Input label="Desde" type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
        </div>
        <div className="w-40">
          <Input label="Hasta" type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
        </div>
        <Button type="submit" variant="secondary">Filtrar</Button>
        <Button
          variant="ghost"
          onClick={() => {
            setDraft(EMPTY_FILTERS);
            setFilters(EMPTY_FILTERS);
          }}
        >
          Limpiar
        </Button>
      </form>

      <div aria-live="polite">{error && <Banner tone="error">{error}</Banner>}</div>

      <Table
        caption="Registros de auditoría"
        columns={columns}
        rows={items}
        rowKey={(e) => e.id}
        loading={loading}
        stickyHeader
        maxHeight={LIST_MAX_HEIGHT}
        empty={<EmptyState title="Sin registros" description="No hay acciones que coincidan con los filtros." />}
      />
      {nextCursor && !loading && (
        <div className="flex justify-center">
          <Button variant="secondary" loading={loadingMore} onClick={() => void load(nextCursor)}>Cargar más</Button>
        </div>
      )}
      <p className="text-xs text-ink-muted">
        Registros inmutables: no hay acción de editar ni borrar. Nunca muestran secretos, contraseñas, códigos de segundo factor ni IPs.
      </p>

      <Modal open={detail !== null} onClose={closeDetail} title={detail ? actionLabel(detail.action) : ''} size="lg">
        {detail && (
          <>
            <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-ink-muted">Fecha</dt>
              <dd className="tabular-nums">{formatDateTime(detail.at)}</dd>
              <dt className="text-ink-muted">Autor</dt>
              <dd>{actorLabel(detail.actor, names)}</dd>
              <dt className="text-ink-muted">Origen</dt>
              <dd>{originLabel(detail.origin)}</dd>
              <dt className="text-ink-muted">Recurso</dt>
              <dd className="break-all font-mono text-xs">{detail.resource}</dd>
              {detail.correlationId && (
                <>
                  <dt className="text-ink-muted">Correlación</dt>
                  <dd className="break-all font-mono text-xs">{detail.correlationId}</dd>
                </>
              )}
            </dl>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <h3 className="mb-1 text-xs font-semibold text-ink-muted">Antes</h3>
                <pre className="max-h-64 overflow-auto rounded-sm bg-mist p-2 font-mono text-xs">{JSON.stringify(detail.before ?? null, null, 2)}</pre>
              </div>
              <div>
                <h3 className="mb-1 text-xs font-semibold text-ink-muted">Después</h3>
                <pre className="max-h-64 overflow-auto rounded-sm bg-mist p-2 font-mono text-xs">{JSON.stringify(detail.after ?? null, null, 2)}</pre>
              </div>
            </div>
          </>
        )}
      </Modal>
    </section>
  );
}
